import {
  ENVIRONMENT_TONE_COLORS,
  environmentCue,
  type EnvironmentCue,
} from "@/shared/lib/env/environment"
import { cn } from "@beringlab/ui"

// Above Radix overlays (z-50) and toasts (9999): a setting is usually changed in
// a dialog, and the dialog is exactly where the frame must still show.
const TOP_LAYER = 2147483647

/**
 * Frames the viewport on anything but a deployed production build, so the
 * environment is in peripheral vision on every page, login and dialogs
 * included. It never takes a click and never moves layout.
 */
export const EnvironmentFrame = ({
  cue = environmentCue,
}: {
  cue?: EnvironmentCue
}) => {
  if (!cue.pervasive) return null
  const { fill, ink } = ENVIRONMENT_TONE_COLORS[cue.tone]

  return (
    <div
      data-environment-frame={cue.tone}
      className="pointer-events-none fixed inset-0 select-none border-2 print:hidden"
      style={{ borderColor: fill, zIndex: TOP_LAYER }}
    >
      <div
        className="absolute bottom-0 left-0 rounded-tr-md px-2 py-0.5 text-[11px] font-semibold leading-4"
        style={{ backgroundColor: fill, color: ink }}
      >
        {cue.label}
        <span className="hidden sm:inline"> · {cue.detail}</span>
      </div>
    </div>
  )
}

/**
 * For headers where staff change settings: shown in every environment, PROD
 * included, so the absence of a frame is never the only signal.
 */
export const EnvironmentBadge = ({
  cue = environmentCue,
  className,
}: {
  cue?: EnvironmentCue
  className?: string
}) => {
  const { fill, ink } = ENVIRONMENT_TONE_COLORS[cue.tone]

  return (
    <span
      title={`API: ${cue.detail}`}
      className={cn(
        "inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded px-1.5 text-[11px] font-bold leading-none tracking-wide",
        className
      )}
      style={{ backgroundColor: fill, color: ink }}
    >
      {cue.label}
    </span>
  )
}
